import React from 'react';
import { cn } from '../../cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warn'
  | 'outline'
  | 'text'
  | 'glass-light'
  | 'glass-medium'
  | 'glass-heavy'
  | 'default'
  | 'ghost'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'default' | 'icon';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  // Radix-style compatibility prop; ignored by the underlying button
  asChild?: boolean;
  // Voice-ready flags
  'data-voice-ready'?: boolean;
  'data-voice-id'?: string;
  'data-voice-role'?: 'action' | 'nav' | 'status' | 'field';
};

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-accent-strong text-on-color-accent hover:opacity-90 hover:shadow-md',
  // Alias "default" → primary for backward compatibility
  default: 'bg-accent-strong text-on-color-accent hover:opacity-90 hover:shadow-md',
  secondary: 'bg-muted text-muted-foreground hover:opacity-90 hover:shadow-md',
  success: 'bg-success text-success-foreground hover:opacity-90 hover:shadow-md',
  danger: 'bg-danger text-danger-foreground hover:opacity-90 hover:shadow-md',
  warn: 'bg-warn text-warn-foreground hover:opacity-90 hover:shadow-md',
  outline: 'bg-transparent border border-border text-foreground hover:bg-accent/15 hover:border-accent/40 hover:text-accent',
  text: 'bg-transparent text-accent hover:bg-accent/15',
  // "ghost" behaves like a subtle text-style button — hover reveals subtle fill
  ghost: 'bg-transparent text-foreground hover:bg-accent/15 hover:text-accent',
  // "link" appears as text with accent color
  link: 'bg-transparent text-accent underline underline-offset-2 hover:text-accent/80',
  'glass-light': 'bg-glass-light backdrop-blur-glass-light border border-glass-light text-foreground hover:opacity-90',
  'glass-medium': 'bg-glass-medium backdrop-blur-glass-medium border border-glass-medium text-foreground hover:opacity-90',
  'glass-heavy': 'bg-glass-heavy backdrop-blur-glass-heavy border border-glass-heavy text-foreground hover:opacity-90',
};

const sizeStyles: Record<ButtonSize, string> = {
  // Unified small size for header controls (consistent 32px hit area)
  sm: 'text-sm px-3 py-1.5 min-h-8',
  md: 'text-sm px-4 py-2 min-h-10',
  lg: 'text-base px-5 py-2.5 min-h-touch',
  // Alias "default" → md
  default: 'text-sm px-4 py-2 min-h-10',
  // Icon-only buttons: square hit area, centered icon
  icon: 'p-2 min-h-8 min-w-8 flex items-center justify-center',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    className = '',
    disabled = false,
    icon,
    onClick,
    type = 'button',
    asChild: _asChild, // accepted for compatibility but not used
    ...rest
  },
  ref
) {
  return (
    <button
      data-component="Button"
      data-package="@appic/ui-components"
      data-testid="button"
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        variantStyles[variant],
        sizeStyles[size],
        'rounded-lg font-medium flex items-center justify-center',
        'transition-all duration-150 shadow-sm',
        'active:scale-95 active:shadow-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        'whitespace-normal break-words min-w-0',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...rest}
    >
      {icon && <span className={`${children ? 'mr-2' : ''}`}>{icon}</span>}
      {children}
    </button>
  );
});

// Backwards-compatible variant helper used by pagination and other UI utilities
export function buttonVariants(opts: { variant?: ButtonVariant; size?: ButtonSize } = {}): string {
  const { variant = 'primary', size = 'md' } = opts;
  const variantClass = variantStyles[variant];
  const sizeClass = sizeStyles[size];
  return cn(
    variantClass,
    sizeClass,
    'rounded-lg font-medium flex items-center justify-center',
    'transition-colors shadow-sm',
    'focus-visible:outline-none'
  );
}
